import { CdkMenuTrigger } from '@angular/cdk/menu';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActionDialog, SiActionDialogService } from '@siemens/element-ng/action-modal';
import { SiEmptyStateModule } from '@siemens/element-ng/empty-state';
import { SiIconComponent } from '@siemens/element-ng/icon';
import { SiMenuModule } from '@siemens/element-ng/menu';
import { SiModalService } from '@siemens/element-ng/modal';
import { SiSearchBarModule } from '@siemens/element-ng/search-bar';
import { SelectOption, SiSelectModule } from '@siemens/element-ng/select';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import { SiTooltipModule } from '@siemens/element-ng/tooltip';
import { Subscription } from 'rxjs';
import { STANDARD_MODAL_CONFIG } from 'src/app/main/shared/const';
import {
  SaveData,
  Schedule,
  Track,
  TrackDataSet,
  TrackInSchedule,
  Vehicle,
  Workspace
} from 'src/app/main/shared/DataTypes';
import { BracketPipe } from 'src/app/main/shared/pipes/brackets.pipe';
import { StarPrefixPipe } from 'src/app/main/shared/pipes/star-prefix.pipe';
import { AccountService, UserRole } from 'src/app/main/shared/services/account.service';
import { EventService } from 'src/app/main/shared/services/event.service';
import { ShareDataService } from 'src/app/main/shared/services/share-data.service';
import { SimulationService } from 'src/app/main/shared/services/simulation.service';
import { WorkspaceService } from 'src/app/main/shared/services/workspace.service';

import { LoadModalComponent } from '../load-modal/load-modal.component';
import { SaveModalComponent } from '../save-modal/save-modal.component';
import { TrackService } from '../track/track.service';
import { ScheduleTableComponent } from './schedule-table/schedule-table.component';
import { ScheduleRenamePreview, ScheduleRenameResponse, ScheduleService } from './schedule.service';

@Component({
  selector: 'app-schedule',
  imports: [
    SiEmptyStateModule,
    TranslateModule,
    LoadModalComponent,
    SaveModalComponent,
    SiMenuModule,
    CdkMenuTrigger,
    SiTooltipModule,
    StarPrefixPipe,
    ScheduleTableComponent,
    BracketPipe,
    SiSearchBarModule,
    FormsModule,
    SiIconComponent,
    SiSelectModule
  ],
  templateUrl: './schedule.component.html',
  styleUrl: './schedule.component.scss'
})
export class ScheduleComponent implements AfterViewInit, OnDestroy {
  scheduleService = inject(ScheduleService);
  eventService = inject(EventService);
  accountService = inject(AccountService);
  modalService = inject(SiModalService);
  toastNotificationService = inject(SiToastNotificationService);
  router = inject(Router);
  workspaceService = inject(WorkspaceService);
  simulationService = inject(SimulationService);
  translateService = inject(TranslateService);
  trackService = inject(TrackService);
  siModal = inject(SiActionDialogService);
  shareDataService = inject(ShareDataService);

  ngAfterViewInit(): void {
    // Subscribe to Event to cancel simulation
    this.eventService.cancelSimulationEvent$.subscribe(() => {
      this.cancelSimulation();
    });

    // Receive Track data if 'Open Track in Schedule' is clicked
    this.eventService.moveTrackFromTracksToScheduleEvent$.subscribe((trackVariant: Track) => {
      this.addTrackToSchedule(trackVariant);
    });

    // Receive selected Vehicle
    this.tourDataSubscription.add(
      this.eventService.selectedVehicleChangedEvent$.subscribe((vehicle: Vehicle | null) => {
        this.selectedVehicle.set(vehicle ?? undefined);
      })
    );

    this.eventService.simulatingScheduleChangedEvent$.subscribe(
      (simualtedSchedule: Schedule | null) => {
        if (simualtedSchedule === null) {
          this.simulationRunning.set(false);
          return;
        }
        this.simulationRunning.set(true);
      }
    );

    this.tourDataSubscription.add(
      this.eventService.loadTourScheduleEvent$.subscribe(schedule => {
        this.tourScheduleSnapshot ??= {
          schedule: this.selectedSchedule(),
          stationsValid: this.scheduleTrackStationsValid(),
          versionsAccessible: this.scheduleTrackVersionsAccessible(),
          simulationRunning: this.simulationRunning(),
          simulationKernel: this.selectedSimulationKernel()
        };
        this.selectedSchedule.set(schedule);
      })
    );

    this.tourDataSubscription.add(
      this.eventService.clearTourDataEvent$.subscribe(() => {
        if (!this.tourScheduleSnapshot) {
          return;
        }

        this.selectedSchedule.set(this.tourScheduleSnapshot.schedule);
        this.scheduleTrackStationsValid.set(this.tourScheduleSnapshot.stationsValid);
        this.scheduleTrackVersionsAccessible.set(this.tourScheduleSnapshot.versionsAccessible);
        this.simulationRunning.set(this.tourScheduleSnapshot.simulationRunning);
        this.selectedSimulationKernel.set(this.tourScheduleSnapshot.simulationKernel);
        this.tourScheduleSnapshot = null;
      })
    );
  }

  readonly availableWorkspaces = this.shareDataService.availableWorkspaces;
  readonly selectedSchedule = signal<Schedule | undefined>(undefined);
  readonly selectedVehicle = signal<Vehicle | undefined>(undefined);
  private tourScheduleSnapshot: {
    schedule: Schedule | undefined;
    stationsValid: boolean;
    versionsAccessible: boolean;
    simulationRunning: boolean;
    simulationKernel: string;
  } | null = null;

  // Rename state
  readonly renameInProgress = signal(false);
  readonly canEdit = computed(() => !!this.selectedSchedule()?.id);

  readonly simulationKernelOptions: SelectOption<string>[] = [
    {
      type: 'option',
      label: 'Python',
      value: 'python'
    },
    {
      type: 'option',
      label: 'Rust',
      value: 'rust'
    }
  ];

  readonly showSimulationKernelOptions = computed(() => {
    const runtimeConfig = this.shareDataService.runtimeConfig();
    return runtimeConfig.chooseSimulationKernel;
  });

  readonly selectedSimulationKernel = signal<string>(this.simulationKernelOptions[0].value);

  hasRequiredRole(): boolean {
    return this.accountService.hasRequiredRoles([UserRole.ADMIN, UserRole.PSMI]);
  }

  openRenameModal(template: TemplateRef<any>) {
    this.modalService.show(template, {
      ...STANDARD_MODAL_CONFIG,
      class: 'responsive-modal modal-dialog-centered'
    });
  }

  handleRenameSchedule(data: { name: string; version: string; description: string }) {
    const schedule = this.selectedSchedule();
    if (!schedule?.id) {
      return;
    }

    const trimmedName = data.name.trim();
    const trimmedVersion = data.version.trim();
    const trimmedDescription = data.description.trim();

    if (!trimmedName || !trimmedVersion) {
      this.toastNotificationService.showToastNotification({
        state: 'danger',
        title: this.translateService.instant('SHARED.ERROR'),
        message: this.translateService.instant('SCHEDULE.RENAME.EMPTY_FIELDS')
      });
      return;
    }

    const currentVersion = schedule.version ?? '';
    const currentDescription = schedule.description?.trim() ?? '';
    const nameChanged = trimmedName !== schedule.name;
    const versionChanged = trimmedVersion !== currentVersion;
    const descriptionChanged = trimmedDescription !== currentDescription;

    if (!nameChanged && !versionChanged && !descriptionChanged) {
      return;
    }

    if (!nameChanged) {
      this.executeScheduleRename(schedule.id, trimmedName, trimmedVersion, trimmedDescription);
      return;
    }

    this.scheduleService.previewRenameSchedule(schedule.name).subscribe({
      next: (preview: ScheduleRenamePreview) => {
        const dialog: ActionDialog = {
          type: 'confirmation',
          heading: this.translateService.instant('SCHEDULE.RENAME.CONFIRM_HEADING'),
          message: this.translateService.instant('SCHEDULE.RENAME.CONFIRM_MESSAGE', {
            variantCount: preview.variantCount
          })
        };

        this.siModal.showActionDialog(dialog).subscribe(result => {
          if (result === 'confirm') {
            this.executeScheduleRename(
              schedule.id!,
              trimmedName,
              trimmedVersion,
              trimmedDescription
            );
          }
        });
      },
      error: error => {
        this.showRenameError(error);
      }
    });
  }

  private executeScheduleRename(
    scheduleId: number,
    newName: string,
    newVersion: string,
    newDescription: string
  ) {
    this.renameInProgress.set(true);

    this.scheduleService.renameSchedule(scheduleId, newName, newVersion, newDescription).subscribe({
      next: (response: ScheduleRenameResponse) => {
        this.selectedSchedule.update(schedule => {
          if (!schedule) {
            return schedule;
          }

          return {
            ...schedule,
            name: response.newName,
            version: response.newVersion,
            description: response.newDescription
          };
        });

        this.refreshAvailableWorkspaces();

        this.toastNotificationService.showToastNotification({
          state: 'success',
          title: this.translateService.instant('SHARED.SUCCESS'),
          message: this.translateService.instant('SCHEDULE.RENAME.SUCCESS', {
            name: response.newName,
            version: response.newVersion
          })
        });
      },
      error: error => {
        this.showRenameError(error);
      },
      complete: () => {
        this.renameInProgress.set(false);
      }
    });
  }

  private refreshAvailableWorkspaces() {
    this.workspaceService.getAllWorkspaces().subscribe({
      next: (workspaces: Workspace[]) => {
        this.shareDataService.setAvailableWorkspaces(workspaces);
      }
    });
  }

  private showRenameError(error: any) {
    this.renameInProgress.set(false);
    const message =
      error?.error?.detail ??
      error?.message ??
      error?.statusText ??
      this.translateService.instant('SCHEDULE.RENAME.FALLBACK_ERROR');

    this.toastNotificationService.showToastNotification({
      state: 'danger',
      title: this.translateService.instant('SHARED.ERROR'),
      message
    });
  }

  private simulationSubscription: Subscription | null = null;
  private readonly tourDataSubscription = new Subscription();

  readonly simulationRunning = signal<boolean>(false);

  readonly simulateButtonEnabled = computed(() => {
    const isValid = this.scheduleTrackStationsValid();
    const isNotRunning = !this.simulationRunning();
    const hasVehicle = !!this.selectedVehicle();
    return isValid && isNotRunning && hasVehicle && this.scheduleTrackVersionsAccessible();
  });

  readonly exportButtonEnabled = computed(() => {
    return this.scheduleTrackStationsValid() && this.scheduleTrackVersionsAccessible();
  });

  readonly saveButtonEnabled = computed(() => {
    return this.scheduleTrackStationsValid() && this.scheduleTrackVersionsAccessible();
  });

  readonly simulateButtonTooltip = computed(() => {
    const reasons: string[] = [];

    if (!this.scheduleTrackStationsValid()) {
      reasons.push(this.translateService.instant('SCHEDULE.SIMULATE_BUTTON_TOOLTIP.INVALID_DATA'));
    }
    if (!this.scheduleTrackVersionsAccessible()) {
      reasons.push(
        this.translateService.instant('SCHEDULE.SIMULATE_BUTTON_TOOLTIP.INACCESSIBLE_TRACK')
      );
    }
    if (this.simulationRunning()) {
      reasons.push(
        this.translateService.instant('SCHEDULE.SIMULATE_BUTTON_TOOLTIP.ALREADY_RUNNING')
      );
    }
    if (!this.selectedVehicle()) {
      reasons.push(this.translateService.instant('SCHEDULE.SIMULATE_BUTTON_TOOLTIP.NO_VEHICLE'));
    }

    return reasons.length > 0
      ? reasons.join(' ')
      : this.translateService.instant('SCHEDULE.SIMULATE_BUTTON_TOOLTIP.CLICK_TO_START');
  });

  readonly scheduleTrackStationsValid = signal(false);
  readonly scheduleTrackVersionsAccessible = signal(true);

  // Cache used to temporarily store the track parameters and the index of the track within the schedule
  // during the period between opening the save modal and receiving the user input from the modal.
  // This is necessary because when saving a normal track/schedule, the currently opened track/schedule
  // just gets overwritten with the new data.
  private saveUnsavedTrackInScheduleCache: {
    indexInSchedule: number;
    parameters: { [key: string]: TrackDataSet[] };
  } | null = null;

  // Array to store unsaved tracks information
  // to map the unsaved track that gets clicked in the modal to the actual track in the schedule.
  private unsavedTracksInfos: { indexInSchedule: number; track: TrackInSchedule }[] = [];

  addTrackToSchedule(track: Track) {
    const trackToAdd = { ...track };
    const selectedSchedule = this.selectedSchedule();
    if (selectedSchedule) {
      // Add new Track to existing Schedule
      this.selectedSchedule.set({
        ...selectedSchedule,
        tracks: [
          ...selectedSchedule.tracks,
          this.scheduleService.getScheduleTracksFromTrackVariant(trackToAdd)
        ]
      });
    } else {
      // Create new Schedule with the selected Track
      this.selectedSchedule.set(this.scheduleService.getScheduleFromTrack(trackToAdd));
    }
  }

  openModal(template: TemplateRef<any>): void {
    this.modalService.show(template, {
      ...STANDARD_MODAL_CONFIG,
      class: 'medium-modal'
    });
  }

  logTest(): void {
    console.log('Test log from ScheduleComponent');
  }

  openSaveTrackModal(template: TemplateRef<any>, track: Track): void {
    this.modalService.show(template, {
      ...STANDARD_MODAL_CONFIG,
      class: 'responsive-modal modal-dialog-centered',
      inputValues: {
        track: track
      }
    });
  }

  openSaveScheduleModal(template: TemplateRef<any>): void {
    this.modalService.show(template, {
      ...STANDARD_MODAL_CONFIG,
      class: 'responsive-modal modal-dialog-centered'
    });
  }

  closeModal(modalRef: any): void {
    modalRef.hide();
  }

  openScheduleClicked(schedule: any) {
    this.scheduleService
      .getScheduleTracksFromDB(schedule)
      .subscribe((tracks: TrackInSchedule[]) => {
        this.selectedSchedule.set({
          ...schedule,
          tracks: tracks
        });
      });
  }

  handleSaveNewSchedule(saveData: SaveData) {
    if (this.selectedSchedule()) {
      // Create new Schedule and Variant objects
      const newSchedule: Schedule = {
        ...this.selectedSchedule()!,
        name: saveData.name,
        version: saveData.version,
        description: saveData.description,
        deleted: false
      };

      // Save new Schedule to DB
      this.scheduleService.saveNewScheduleToDB(newSchedule, saveData.workspaceId).subscribe({
        next: (response: any) => {
          // Update available workspaces to receive the new schedule
          this.workspaceService.getAllWorkspaces().subscribe({
            next: (workspaces: Workspace[]) => {
              this.shareDataService.setAvailableWorkspaces(workspaces);
            }
          });
          newSchedule.id = response.id;
          this.selectedSchedule.set(newSchedule);
        },
        error: e => {
          this.toastNotificationService.showToastNotification({
            state: 'danger',
            title: this.translateService.instant('SHARED.ERROR'),
            message:
              this.translateService.instant('SHARED.ERROR_SAVE', {
                type: this.translateService.instant('SCHEDULE.SCHEDULE')
              }) +
              ': ' +
              e.statusText
          });
        },
        complete: () => {
          this.toastNotificationService.showToastNotification({
            state: 'success',
            title: this.translateService.instant('SHARED.SUCCESS'),
            message: this.translateService.instant('SHARED.SUCCESS_SAVE', {
              type: this.translateService.instant('SCHEDULE.SCHEDULE'),
              name: saveData.name
            })
          });
        }
      });
    }
  }

  startSimulation() {
    this.toastNotificationService.showToastNotification({
      state: 'info',
      title: this.translateService.instant('SCHEDULE.SIMULATION_STARTED'),
      message: this.translateService.instant('SCHEDULE.SIMULATION_STARTED', {
        schedule: this.selectedSchedule()!.name
      })
    });
    this.eventService.updateSimulatingSchedule(this.selectedSchedule()!);
    this.eventService.setLastSimulatedSchedule(this.selectedSchedule()!);
    this.simulationSubscription = this.simulationService
      .simulate(this.selectedSchedule()!, this.selectedSimulationKernel())
      .subscribe({
        next: (results: any) => {
          this.eventService.updateSimulationResults(results);
        },
        complete: () => {
          this.eventService.updateSimulatingSchedule(null);
          this.toastNotificationService.showToastNotification({
            state: 'success',
            title: this.translateService.instant('SCHEDULE.SIMULATION_FINISHED'),
            message: '',
            action: {
              title: this.translateService.instant('SCHEDULE.SIMULATION_FINISHED_MESSAGE'),
              action: () => this.router.navigate(['/', 'main', 'results'])
            }
          });
        },
        error: (e: any) => {
          this.eventService.updateSimulatingSchedule(null);
          this.toastNotificationService.showToastNotification({
            state: 'danger',
            title: this.translateService.instant('SCHEDULE.SIMULATION_FAILED'),
            message: e.statusText
          });
        }
      });
  }

  startSimulationAndExport() {
    this.eventService.updateSimulatingSchedule(this.selectedSchedule()!);
    this.simulationSubscription = this.simulationService
      .simulateAndExport(this.selectedSchedule()!, this.selectedSimulationKernel())
      .subscribe({
        next: () => {
          this.toastNotificationService.showToastNotification({
            state: 'success',
            title: this.translateService.instant('SCHEDULE.SIMULATION_FINISHED'),
            message: this.translateService.instant('SCHEDULE.SIMULATION_FINISHED_MESSAGE')
          });
        },
        error: (e: any) => {
          this.toastNotificationService.showToastNotification({
            state: 'danger',
            title: this.translateService.instant('SCHEDULE.SIMULATION_FAILED'),
            message: e.statusText
          });
        },
        complete: () => {
          this.eventService.updateSimulatingSchedule(null);
        }
      });
  }

  cancelSimulation() {
    if (this.simulationSubscription) {
      this.simulationSubscription.unsubscribe();
      this.simulationSubscription = null;

      this.toastNotificationService.showToastNotification({
        state: 'info',
        title: this.translateService.instant('SCHEDULE.SIMULATION_CANCELLED'),
        message: this.translateService.instant('SCHEDULE.SIMULATION_CANCELLED_MESSAGE')
      });

      this.eventService.updateSimulatingSchedule(null);
    }
  }

  removeTrack(index: number) {
    this.selectedSchedule.update(schedule => {
      if (!schedule) {
        return schedule; // Return as is if undefined
      }
      const updatedTracks = [...schedule.tracks];
      updatedTracks.splice(index, 1);
      return { ...schedule, tracks: updatedTracks };
    });
  }

  ngOnDestroy(): void {
    // Unsubscribe from the simulation observable if it exists
    if (this.simulationSubscription) {
      this.simulationSubscription.unsubscribe();
    }
    this.tourDataSubscription.unsubscribe();
  }

  readonly jsonFileInput = viewChild<ElementRef>('jsonFileInput');
  readonly csvFileInput = viewChild<ElementRef>('csvFileInput');

  openFileSelector(type: 'json' | 'csv') {
    if (type === 'json') {
      this.jsonFileInput()?.nativeElement.click();
    } else {
      this.csvFileInput()?.nativeElement.click();
    }
  }

  onJSONFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const jsonData: unknown = JSON.parse(reader.result as string);

        const schedule = this.scheduleService.getScheduleFromJSON(jsonData);
        this.selectedSchedule.set(schedule);

        const vehicle = this.scheduleService.getVehicleFromJSON(jsonData);
        if (vehicle) {
          this.selectedVehicle.set(vehicle);
          this.eventService.updateSelectedVehicle(vehicle);
        }

        this.displayFileImportSuccessToast();
      } catch (error) {
        const type = this.translateService.instant('SCHEDULE.SCHEDULE');
        const message =
          error instanceof Error
            ? error.message
            : this.translateService.instant('SHARED.ERROR_LOAD', {
                type: type
              });

        this.toastNotificationService.showToastNotification({
          state: 'danger',
          title: this.translateService.instant('SHARED.ERROR_LOAD', {
            type: type
          }),
          message
        });
      } finally {
        input.value = '';
      }
    };

    reader.readAsText(file);
  }

  onCSVFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const schedule = this.scheduleService.getScheduleFromCSV(reader.result as string);
        this.selectedSchedule.set(schedule);

        this.displayFileImportSuccessToast();
      } catch (error) {
        const type = this.translateService.instant('SCHEDULE.SCHEDULE');
        const message =
          error instanceof Error
            ? error.message
            : this.translateService.instant('SHARED.ERROR_LOAD', {
                type: type
              });

        this.toastNotificationService.showToastNotification({
          state: 'danger',
          title: this.translateService.instant('SHARED.ERROR_LOAD', {
            type: type
          }),
          message
        });
      } finally {
        input.value = '';
      }
    };

    reader.readAsText(file);
  }

  displayFileImportSuccessToast() {
    this.toastNotificationService.showToastNotification({
      state: 'success',
      title: this.translateService.instant('SHARED.SUCCESS'),
      message: this.translateService.instant('SHARED.SUCCESS_LOAD', {
        type: this.translateService.instant('SCHEDULE.SCHEDULE')
      })
    });
  }

  exportScheduleDataAsDRIVE() {
    this.scheduleService.getDRIVEFileFromSchedule(this.selectedSchedule()!).subscribe({
      next: (response: Blob) => {
        const blob = new Blob([response], {
          type: 'application/octet-stream'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.selectedSchedule()?.name ?? 'export'}.DRiVe`;
        this.simulateLinkClick(link);
        URL.revokeObjectURL(url);
      },
      error: (e: Error) => {
        this.toastNotificationService.showToastNotification({
          state: 'danger',
          title: this.translateService.instant('SHARED.ERROR_EXPORT', {
            type: this.translateService.instant('SCHEDULE.SCHEDULE')
          }),
          message: e.message
        });
      }
    });
  }

  exportScheduleDataAsJSON() {
    const scheduleAndVehicleData = {
      schedule: this.selectedSchedule(),
      vehicle: this.selectedVehicle()
    };

    // Convert the JSON object to a string
    const jsonString = JSON.stringify(scheduleAndVehicleData, null, 2);

    // Create a Blob from the JSON string
    const blob = new Blob([jsonString], { type: 'application/json' });

    // Create an object URL from the Blob
    const url = URL.createObjectURL(blob);

    // Create a link element for downloading
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.selectedSchedule()?.name ?? 'export'}.json`;

    // Simulate the link click to trigger the download
    this.simulateLinkClick(link);

    // Revoke the object URL to free up memory
    URL.revokeObjectURL(url);
  }

  exportScheduleDataAsCSV() {
    const csv = this.scheduleService.getCSVFromSchedule(this.selectedSchedule()!);

    // Prepend a BOM so spreadsheet applications detect the UTF-8 encoding
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.selectedSchedule()?.name ?? 'export'}.csv`;

    this.simulateLinkClick(link);

    URL.revokeObjectURL(url);
  }

  simulateLinkClick(link: HTMLAnchorElement) {
    // Append the link to the document body
    document.body.appendChild(link);
    // Trigger the click event on the link to initiate download
    link.click();
    // Clean up: remove the link and revoke the URL
    document.body.removeChild(link);
  }

  createNewSchedule() {
    this.selectedSchedule.set({
      name: 'New Schedule',
      tracks: [],
      deleted: false,
      tags: []
    });
  }

  openTrackClicked(track: any) {
    const newTrack = { ...track };
    const trackId = track.id;
    // Check if track data already got initialized
    if (!track.parameters && trackId) {
      this.trackService.getTrackDataFromDB(trackId).subscribe((parameters: any) => {
        newTrack.parameters = parameters;
        this.addTrackToSchedule(newTrack);
      });
    } else {
      this.addTrackToSchedule(newTrack);
    }
  }

  closeSchedule() {
    this.selectedSchedule.set(undefined);
  }

  saveButtonClicked(
    saveScheduleModalTemplate: TemplateRef<any>,
    saveUnsavedTracksModalTemplate: TemplateRef<any>
  ) {
    // Check if there are unsaved tracks in the schedule and populate the unsavedTracksInfos array
    this.unsavedTracksInfos = [];
    this.selectedSchedule()!.tracks.forEach((track: TrackInSchedule, index: number) => {
      if (track.trackParameters) {
        this.unsavedTracksInfos.push({ indexInSchedule: index, track });
      }
    });
    if (this.unsavedTracksInfos.length > 0) {
      // Open modal to save unsaved tracks
      this.modalService.show(saveUnsavedTracksModalTemplate, {
        ...STANDARD_MODAL_CONFIG,
        inputValues: { unsavedTrackInfos: this.unsavedTracksInfos },
        class: 'responsive-modal modal-dialog-centered'
      });
    } else {
      // Open modal to save schedule
      this.openSaveScheduleModal(saveScheduleModalTemplate);
    }
  }

  saveTrackInScheduleButtonClicked(
    indexInUnsavedTracks: number,
    saveTrackModalTemplate: TemplateRef<any>
  ) {
    const indexOfTrackInSchedule: number =
      this.unsavedTracksInfos[indexInUnsavedTracks].indexInSchedule;
    const trackInSchedule: TrackInSchedule =
      this.selectedSchedule()!.tracks[indexOfTrackInSchedule];

    const track: Track = {
      name: trackInSchedule.scheduleData.stoppingData[0].trackName,
      parameters: trackInSchedule.trackParameters!,
      tags: [],
      deleted: false,
      source: 'From Scratch'
    };

    // Cache the track parameters for handleSaveNewTrack()
    this.saveUnsavedTrackInScheduleCache = {
      indexInSchedule: indexOfTrackInSchedule,
      parameters: trackInSchedule.trackParameters!
    };

    this.openSaveTrackModal(saveTrackModalTemplate, track);
  }

  handleSaveNewTrack(saveData: SaveData) {
    const newTrack: Track = {
      name: saveData.name,
      version: saveData.version,
      description: saveData.description,
      parameters: this.saveUnsavedTrackInScheduleCache!.parameters!,
      deleted: false,
      tags: [],
      source: 'From Scratch'
    };

    this.trackService.saveNewTrackToDB(newTrack, saveData.workspaceId).subscribe({
      error: e => {
        this.toastNotificationService.showToastNotification({
          state: 'danger',
          title: this.translateService.instant('SHARED.ERROR'),
          message:
            this.translateService.instant('SHARED.ERROR_SAVE', {
              type: this.translateService.instant('TRACK.TRACK')
            }) +
            ': ' +
            e.statusText
        });
      },
      next: (response: any) => {
        // Update available workspaces to receive the new track
        this.workspaceService.getAllWorkspaces().subscribe({
          next: (workspaces: Workspace[]) => {
            this.shareDataService.setAvailableWorkspaces(workspaces);
          }
        });

        // Add the new properties to the track in schedule and mark it as saved
        const oldTrackInSchedule: TrackInSchedule =
          this.selectedSchedule()!.tracks[this.saveUnsavedTrackInScheduleCache!.indexInSchedule];
        const newTrackInSchedule: TrackInSchedule = this.scheduleService.markTrackInScheduleAsSaved(
          newTrack,
          oldTrackInSchedule
        );
        // Update the schedule with the new track
        this.selectedSchedule.update(schedule => {
          if (!schedule) {
            return schedule;
          }
          const updatedTracks = [...schedule.tracks];
          updatedTracks[this.saveUnsavedTrackInScheduleCache!.indexInSchedule] = newTrackInSchedule;
          return { ...schedule, tracks: updatedTracks };
        });
        this.saveUnsavedTrackInScheduleCache = null;
      },
      complete: () => {
        this.toastNotificationService.showToastNotification({
          state: 'success',
          title: this.translateService.instant('SHARED.SUCCESS'),
          message: this.translateService.instant('SHARED.SUCCESS_SAVE', {
            type: this.translateService.instant('TRACK.TRACK'),
            name: saveData.name
          })
        });
      }
    });
  }

  simulateDropdownOpen = false;
}
